/**
 * Shape used by the catalog structure editor (pricing, packages, legacy deliverable, etc.)
 * and Firestore update payload builder (mirrors handleEditItem in alacartetemplate.js).
 */

import { normalizeExclusiveChoiceItemIds } from './alaCarteExclusiveChoice';

export function toCatalogEditDraft(item) {
  if (!item) return null;
  return {
    ...item,
    pricingType: item.pricingType || 'packages',
    parentItemId: item.parentItemId || '',
    standardPricing: {
      ...(item.standardPricing || { cost: 0, unit: 'item' }),
      stripeProductId: (item.standardPricing && item.standardPricing.stripeProductId) || '',
    },
    hasDeliverable: item.hasDeliverable || false,
    popularAddOn: item.popularAddOn === true,
    alwaysRequired: item.alwaysRequired === true,
    minimumTermMonths:
      item.minimumTermMonths === '' || item.minimumTermMonths === undefined || item.minimumTermMonths === null
        ? ''
        : item.minimumTermMonths,
    caseByCaseMultiplier:
      item.caseByCaseMultiplier === '' ||
      item.caseByCaseMultiplier === undefined ||
      item.caseByCaseMultiplier === null
        ? ''
        : item.caseByCaseMultiplier,
    deliverable: {
      quantity: '',
      maxQuantity: '',
      period: 'One Time',
      programs: [],
      ...(item.deliverable && typeof item.deliverable === 'object' ? item.deliverable : {}),
    },
    icon: item.icon || '📦',
    exclusiveChoiceItemIds: normalizeExclusiveChoiceItemIds(item.exclusiveChoiceItemIds, item.id),
  };
}

/**
 * @param {Record<string, unknown>} editingItem
 * @returns {{ ok: true, data: Record<string, unknown> } | { ok: false, message: string }}
 */
export function buildAlaCarteUpdatePayload(editingItem) {
  if (!editingItem?.id) {
    return { ok: false, message: 'Missing item id' };
  }
  if (!editingItem.name || !editingItem.description) {
    return { ok: false, message: 'Name and description are required' };
  }

  let updatedData;

  if (editingItem.pricingType === 'child') {
    if (!editingItem.parentItemId) {
      return { ok: false, message: 'Please select a parent item for this child item.' };
    }
    updatedData = {
      ...editingItem,
      pricingType: 'child',
      parentItemId: editingItem.parentItemId,
      updatedAt: new Date(),
    };
  } else if (editingItem.pricingType === 'caseByCase') {
    updatedData = {
      ...editingItem,
      pricingType: 'caseByCase',
      updatedAt: new Date(),
    };
  } else if (editingItem.pricingType === 'alwaysIncluded') {
    const cost = editingItem.standardPricing?.cost;
    if (cost === '' || cost === undefined || cost === null) {
      return { ok: false, message: 'Please enter the price per month for Always Included items.' };
    }
    updatedData = {
      ...editingItem,
      pricingType: 'alwaysIncluded',
      standardPricing: {
        cost: parseFloat(editingItem.standardPricing?.cost || 0),
        unit: 'month',
        stripeProductId: editingItem.standardPricing?.stripeProductId || '',
      },
      updatedAt: new Date(),
    };
  } else if (editingItem.pricingType === 'packages') {
    const validPackages = (editingItem.packages || []).filter((pkg) => pkg.name && (pkg.items || []).length > 0);
    if (validPackages.length === 0) {
      return { ok: false, message: 'Please add at least one valid package with name and at least one item.' };
    }
    const validatedPackages = validPackages.map((pkg) => ({
      ...pkg,
      items: (pkg.items || [])
        .filter((row) => row.name && row.cost && row.unit)
        .map((row) => ({
          ...row,
          cost: parseFloat(row.cost),
        })),
    }));
    const packagesWithValidItems = validatedPackages.filter((pkg) => pkg.items.length > 0);
    if (packagesWithValidItems.length === 0) {
      return { ok: false, message: 'Please add at least one valid item with name, cost, and unit to your packages.' };
    }
    updatedData = {
      ...editingItem,
      packages: packagesWithValidItems,
      updatedAt: new Date(),
    };
  } else if (editingItem.pricingType === 'term') {
    if (!editingItem.standardPricing?.cost || !editingItem.standardPricing?.unit) {
      return { ok: false, message: 'Please enter both cost and unit for term pricing.' };
    }
    updatedData = {
      ...editingItem,
      pricingType: 'term',
      standardPricing: {
        cost: parseFloat(editingItem.standardPricing.cost),
        unit: editingItem.standardPricing.unit,
        stripeProductId: editingItem.standardPricing?.stripeProductId || '',
      },
      updatedAt: new Date(),
    };
  } else {
    if (!editingItem.standardPricing?.cost || !editingItem.standardPricing?.unit) {
      return { ok: false, message: 'Please enter both cost and unit for standard pricing.' };
    }
    updatedData = {
      ...editingItem,
      standardPricing: {
        cost: parseFloat(editingItem.standardPricing.cost),
        unit: editingItem.standardPricing.unit,
        stripeProductId: editingItem.standardPricing?.stripeProductId || '',
      },
      updatedAt: new Date(),
    };
  }

  const { id, ...rest } = updatedData;
  const exclusiveChoiceItemIds = normalizeExclusiveChoiceItemIds(rest.exclusiveChoiceItemIds, id);
  const popularAddOn = rest.popularAddOn === true;
  // Always-required items are a single deliverable every program includes: pin quantity and max to 1.
  const alwaysRequired = rest.alwaysRequired === true;
  if (alwaysRequired) {
    rest.hasDeliverable = true;
    rest.deliverable = { ...(rest.deliverable || {}), quantity: '1', maxQuantity: '1' };
  }

  // Optional minimum term (months): blank clears it; otherwise a positive whole number.
  let minimumTermMonths = '';
  const rawMinTerm = rest.minimumTermMonths;
  if (rawMinTerm !== '' && rawMinTerm !== undefined && rawMinTerm !== null && String(rawMinTerm).trim() !== '') {
    const parsedMinTerm = parseInt(String(rawMinTerm).trim(), 10);
    if (!Number.isFinite(parsedMinTerm) || parsedMinTerm < 1) {
      return { ok: false, message: 'Minimum term must be a whole number of months (1 or more), or left blank.' };
    }
    minimumTermMonths = parsedMinTerm;
  }

  // Markup multiplier (case-by-case only): blank clears it; otherwise a positive
  // number. Switching an item off case-by-case drops any stale value.
  let caseByCaseMultiplier = '';
  const rawMultiplier = rest.caseByCaseMultiplier;
  if (
    rest.pricingType === 'caseByCase' &&
    rawMultiplier !== '' &&
    rawMultiplier !== undefined &&
    rawMultiplier !== null &&
    String(rawMultiplier).trim() !== ''
  ) {
    const parsedMultiplier = parseFloat(String(rawMultiplier).trim());
    if (!Number.isFinite(parsedMultiplier) || parsedMultiplier <= 0) {
      return { ok: false, message: 'Multiplier must be a positive number, or left blank.' };
    }
    caseByCaseMultiplier = parsedMultiplier;
  }

  return {
    ok: true,
    data: { ...rest, exclusiveChoiceItemIds, popularAddOn, alwaysRequired, minimumTermMonths, caseByCaseMultiplier, id },
  };
}

/** Strip `id` for Firestore updateDoc body (document id is the path). */
export function stripIdForFirestore(data) {
  const { id: _id, ...rest } = data;
  return rest;
}

const COMPLEX_PRICING_TYPES = new Set(['packages', 'alwaysIncluded']);

/**
 * Maps Firestore `pricingType` to simplified team workspace UI mode.
 * @param {string} [pricingType]
 * @returns {'recurring'|'oneOff'|'child'|'caseByCase'|'complex'}
 */
export function teamPricingUiModeFromPricingType(pricingType) {
  if (pricingType === 'term') return 'recurring';
  if (pricingType === 'standard') return 'oneOff';
  if (pricingType === 'child') return 'child';
  if (pricingType === 'caseByCase') return 'caseByCase';
  if (COMPLEX_PRICING_TYPES.has(pricingType)) return 'complex';
  return 'caseByCase';
}

/**
 * Partial Firestore update for team workspace saves (identity, scope, deliverables, mirrored teamInput, optional pricing).
 * When `pricingMode` is `complex`, pricing fields on the document are left unchanged.
 *
 * @param {object} input
 * @returns {{ ok: true, data: Record<string, unknown> } | { ok: false, message: string }}
 */
export function buildTeamWorkspaceUpdatePayload(input) {
  const {
    name,
    description,
    icon,
    scope,
    deliverableRows,
    internalOnlyNotes,
    internalCostEstimate,
    pricingMode,
    parentItemId,
    standardCost,
    standardUnit,
    existingStandardPricing,
  } = input;

  const n = String(name || '').trim();
  if (!n) return { ok: false, message: 'Name is required' };
  if (description === undefined || description === null || String(description).trim() === '') {
    return { ok: false, message: 'Description is required' };
  }

  const stripeProductId = (existingStandardPricing && existingStandardPricing.stripeProductId) || '';

  const hasInternalEst =
    internalCostEstimate !== '' &&
    internalCostEstimate !== undefined &&
    internalCostEstimate !== null &&
    String(internalCostEstimate).trim() !== '';
  let internalEstValue = '';
  if (hasInternalEst) {
    const parsedEst = parseFloat(String(internalCostEstimate));
    if (!Number.isFinite(parsedEst) || parsedEst < 0) {
      return { ok: false, message: 'Enter a valid internal cost estimate, or leave it blank.' };
    }
    internalEstValue = parsedEst;
  }

  const base = {
    name: n,
    description: String(description),
    icon: icon || '📦',
    scope: typeof scope === 'string' ? scope : '',
    deliverableRows: Array.isArray(deliverableRows) ? deliverableRows : [],
    internalOnly: { notes: internalOnlyNotes || '' },
    teamInput: {
      scope: typeof scope === 'string' ? scope : '',
      deliverableRows: Array.isArray(deliverableRows) ? deliverableRows : [],
      internalCostEstimate: internalEstValue,
    },
    updatedAt: new Date(),
  };

  if (pricingMode === 'complex') {
    return { ok: true, data: base };
  }

  const pricingType =
    pricingMode === 'recurring'
      ? 'term'
      : pricingMode === 'oneOff'
        ? 'standard'
        : pricingMode === 'child'
          ? 'child'
          : pricingMode === 'caseByCase'
            ? 'caseByCase'
            : null;

  if (!pricingType) {
    return { ok: false, message: 'Invalid pricing mode.' };
  }

  base.pricingType = pricingType;

  if (pricingType === 'child') {
    if (!parentItemId) return { ok: false, message: 'Select a parent item for child pricing.' };
    base.parentItemId = parentItemId;
  } else {
    base.parentItemId = '';
  }

  if (pricingType === 'term' || pricingType === 'standard') {
    const cost = standardCost;
    const unit = String(standardUnit || '').trim();
    if (cost === '' || cost === undefined || cost === null || Number.isNaN(parseFloat(cost))) {
      return { ok: false, message: 'Enter a valid price for this pricing mode.' };
    }
    if (!unit) return { ok: false, message: 'Select a unit.' };
    base.standardPricing = {
      cost: parseFloat(cost),
      unit,
      stripeProductId,
    };
  } else if (pricingType === 'caseByCase') {
    const raw = standardCost;
    const hasPrice = raw !== '' && raw !== undefined && raw !== null && String(raw).trim() !== '';
    const c = hasPrice ? parseFloat(raw) : 0;
    if (hasPrice && (!Number.isFinite(c) || c < 0)) {
      return { ok: false, message: 'Enter a valid price or leave pricing blank for case-by-case.' };
    }
    const unit = String(standardUnit || 'item').trim() || 'item';
    base.standardPricing = {
      cost: hasPrice ? c : 0,
      unit,
      stripeProductId,
    };
  }

  return { ok: true, data: base };
}
